/* Reusable quantity-offer purchase mode.
   Prices and compare-at prices come from Shopify variants. The tier percentages
   are editable DISPLAY CONFIGURATION and must match the live discount rules.
   Shopify/Koala apply real discounts in the cart; this code adds products only. */
(() => {
  'use strict';

  function init(root) {
    if (!root || root.dataset.fpsQtyInitialized === 'true') return;
    root.dataset.fpsQtyInitialized = 'true';
    const $ = (selector) => root.querySelector(selector);
    const $$ = (selector) => Array.from(root.querySelectorAll(selector));
    const max = Math.max(4, Math.min(30, Number(root.dataset.fpsMax) || 20));
    const clampPct = (value) => Math.max(0, Math.min(90, Number(value) || 0));
    const config = {
      main: {
        second: clampPct(root.dataset.fpsMainSecond),
        third: clampPct(root.dataset.fpsMainThird),
        extra: clampPct(root.dataset.fpsMainExtra),
        singular: root.dataset.fpsMainSingular || 'article',
        plural: root.dataset.fpsMainPlural || 'articles',
        summaryName: root.dataset.fpsMainSummaryName || '',
        tierText: [root.dataset.fpsMainTierOne, root.dataset.fpsMainTierTwo, root.dataset.fpsMainTierThree]
      },
      secondary: {
        second: clampPct(root.dataset.fpsSecondSecond),
        third: clampPct(root.dataset.fpsSecondThird),
        extra: clampPct(root.dataset.fpsSecondExtra),
        singular: root.dataset.fpsSecondSingular || 'article',
        plural: root.dataset.fpsSecondPlural || 'articles',
        summaryName: root.dataset.fpsSecondSummaryName || '',
        tierText: [root.dataset.fpsSecondTierOne, root.dataset.fpsSecondTierTwo, root.dataset.fpsSecondTierThree]
      }
    };
    const word = (name, fallback = '') => root.dataset[name] || fallback;
    const renderText = (template, values) => String(template || '').replace(/\{([a-zA-Z]+)\}/g, (match, key) =>
      Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : match);
    const showCompare = root.dataset.fpsShowCompare !== 'false';
    const showSavings = root.dataset.fpsShowSavings !== 'false';
    const boxMode = root.dataset.fpsBoxMode || 'off';
    const pairedBox = boxMode === 'paired';
    const syncBoxToMainEnabled = boxMode === 'optional' && root.dataset.fpsBoxSyncMain === 'true';
    const bundleNoun = {
      singular: root.dataset.fpsBundleSingular || 'ensemble cadeau',
      plural: root.dataset.fpsBundlePlural || 'ensembles cadeaux'
    };
    const rewardCartMode = root.dataset.fpsRewardsCartMode || 'koala';
    const rewardsEnabled = root.dataset.fpsRewardsEnabled === 'true';
    const boxShowCompare = root.dataset.fpsBoxShowCompare !== 'false';
    const boxShowBadge = root.dataset.fpsBoxShowBadge !== 'false';
    const currency = root.dataset.fpsCurrency || 'CAD';
    const narrowCad = currency === 'CAD'; // Match the original fixed-bundle display: '$' rather than 'CA$'.
    const locale = (root.dataset.fpsLocale || 'fr').replace('_', '-');
    const formatter = (() => {
      try { return new Intl.NumberFormat(locale, { style: 'currency', currency, currencyDisplay: narrowCad ? 'narrowSymbol' : 'symbol', minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
      catch (_) { return null; }
    })();
    const money = (cents) => formatter ? formatter.format(cents / 100) : (narrowCad ? `$${(cents / 100).toFixed(2)}` : `${(cents / 100).toFixed(2)} ${currency}`);
    // Only promotional live currency amounts are compacted. Leave purchase
    // prices, crossed-out prices and cart/savings cards at their existing
    // precision. A whole amount loses .00; a fractional amount keeps two digits.
    const wholeMoneyFormatter = (() => {
      try { return new Intl.NumberFormat(locale, {style: 'currency', currency, currencyDisplay: narrowCad ? 'narrowSymbol' : 'symbol', minimumFractionDigits: 0, maximumFractionDigits: 0}); }
      catch (_) { return null; }
    })();
    const moneyPromo = (cents) => {
      if (!Number.isFinite(cents) || Math.round(cents) % 100 !== 0) return money(cents);
      if (wholeMoneyFormatter) return wholeMoneyFormatter.format(cents / 100);
      return money(cents).replace(/([.,])00(?=\D*$)/, '');
    };
    // The same EUR reference settings used by the fixed bundle also apply here.
    // They are display-only. A blank/invalid reference leaves Shopify pricing intact.
    const sectionRoot = root.closest('.fps');
    const eurReferences = currency === 'EUR' ? {
      main: Number(sectionRoot?.dataset.fpsEurMainReference) || 0,
      secondary: Number(sectionRoot?.dataset.fpsEurSecondaryReference) || 0,
      box: Number(sectionRoot?.dataset.fpsEurBoxReference) || 0
    } : {main: 0, secondary: 0, box: 0};
    const comparePrice = (group, price, shopCompare) => {
      const manual = eurReferences[group] || 0;
      return manual > price ? manual : Math.max(price, Number(shopCompare) || 0);
    };
    const selectedOption = (control) => control?.tagName === 'SELECT' ? control.selectedOptions[0] : control;
    const getVariant = (node, group = 'main') => {
      const value = selectedOption(node);
      const price = Number(value?.dataset.price) || 0;
      return {
        id: Number(value?.value) || 0,
        price,
        compare: comparePrice(group, price, value?.dataset.compare),
        available: value?.dataset.available === 'true'
      };
    };
    const mainVariant = $('[data-fps-qty-main-variant]');
    const perItemMain = root.dataset.fpsIndividualMain === 'true' && mainVariant?.tagName === 'SELECT';
    const mainItemTemplate = $('[data-fps-qty-main-item-template]');
    const mainItemTierShells = $$('[data-fps-qty-main-tier-shell]');
    const mainOptions = mainVariant?.tagName === 'SELECT' ? Array.from(mainVariant.options) : [];
    const defaultMainOption = () => mainOptions.find(o => o.value === mainVariant.value && !o.disabled) || mainOptions.find(o => !o.disabled);
    const mainOptionById = (id) => mainOptions.find(o => Number(o.value) === Number(id) && !o.disabled) || defaultMainOption();
    const mainOptionButtons = $$('[data-fps-qty-main-option]');
    // Match the original bundle's visual picker while preserving the existing
    // quantity-mode select as the ONLY source of variant ID, price and stock.
    function syncMainVisualOptions() {
      if (!mainOptionButtons.length || mainVariant?.tagName !== 'SELECT') return;
      const selected = mainVariant.selectedOptions[0];
      const candidates = Array.from(mainVariant.options);
      for (const button of mainOptionButtons) {
        const index = Number(button.dataset.fpsQtyOptionIndex);
        const value = button.dataset.fpsQtyOptionValue || '';
        const active = selected?.dataset[`option${index}`] === value;
        button.classList.toggle('is-selected', active);
        button.setAttribute('aria-pressed', String(active));
        // Disable only values with no purchasable variant; valid multi-option
        // combinations are resolved on click without inventing a variant.
        button.disabled = !candidates.some((variant) =>
          variant.dataset.available === 'true' && variant.dataset[`option${index}`] === value);
      }
    }
    function selectMainVisualOption(button) {
      if (!mainVariant || mainVariant.tagName !== 'SELECT' || button.disabled) return;
      const changedIndex = Number(button.dataset.fpsQtyOptionIndex);
      const changedValue = button.dataset.fpsQtyOptionValue || '';
      const original = mainVariant.selectedOptions[0];
      if (!changedIndex || changedIndex > 3 || !original) return;
      const choices = [original.dataset.option1, original.dataset.option2, original.dataset.option3];
      choices[changedIndex - 1] = changedValue;
      const candidates = Array.from(mainVariant.options).filter((variant) => variant.dataset.available === 'true');
      // First preserve all other chosen options. If the combination does not
      // exist, prefer an available variant with the newly selected value and
      // the maximum number of remaining options unchanged (same as Mode 1).
      const exact = candidates.find((variant) => [1, 2, 3].every((index) =>
        !choices[index - 1] || variant.dataset[`option${index}`] === choices[index - 1]));
      const related = candidates.filter((variant) => variant.dataset[`option${changedIndex}`] === changedValue);
      related.sort((a, b) => {
        const score = (variant) => [1, 2, 3].reduce((sum, index) =>
          sum + (index !== changedIndex && choices[index - 1] && variant.dataset[`option${index}`] === choices[index - 1] ? 1 : 0), 0);
        return score(b) - score(a);
      });
      const match = exact || related[0];
      if (!match) return;
      if (mainVariant.value !== match.value) {
        mainVariant.value = match.value;
        mainVariant.dispatchEvent(new Event('change', {bubbles: true}));
      } else {
        syncMainVisualOptions();
      }
    }
    function showSelectedMainVariantMedia() {
      const mediaId = mainVariant?.selectedOptions?.[0]?.dataset.mediaId;
      if (!mediaId) return;
      const thumb = root.closest('.fps')?.querySelector(`[data-fps-gallery-thumb][data-fps-media-id="${mediaId}"]`);
      // The existing gallery owns its own click behavior, slide counter,
      // scroll position and keyboard access. Reuse it without changing Mode 1.
      if (thumb) thumb.click();
    }
    const secondTemplate = $('[data-fps-qty-variant-template]');
    const secondFixed = $('[data-fps-qty-secondary-fixed]');
    const secondFields = $('[data-fps-qty-variant-fields]');
    const secondArea = $('[data-fps-qty-secondary-section]');
    const secondToggle = $('[data-fps-qty-toggle-secondary]');
    const boxOffer = $('[data-fps-qty-box]');
    const boxToggle = $('[data-fps-qty-box-toggle]');
    const boxDetails = $('[data-fps-qty-box-details]');
    const boxChoices = $$('[data-fps-qty-box-variant]');
    const boxSummary = $('[data-fps-qty-summary-box-row]');
    const boxMax = Math.max(1, Number(root.dataset.fpsBoxMax) || 10);
    const quickview = $('[data-fps-qty-quickview]');
    const summarySecond = $('[data-fps-qty-summary-secondary-row]');
    const submit = $('[data-fps-qty-add]');
    const status = $('[data-fps-qty-status]');
    const route = (root.dataset.fpsCartRoot || window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
    const state = {
      main: Math.max(1, Math.min(3, Number(root.dataset.fpsInitialMain) || 2)),
      secondary: 0,
      initialSecondary: Math.max(1, Math.min(3, Number(root.dataset.fpsInitialSecond) || 1)),
      // Independent IDs only when the merchant explicitly enables per-item choice.
      // Keep entries beyond the current quantity so decreasing then increasing
      // the tier does not discard previously chosen variants.
      mainItemIds: [],
      boxOn: pairedBox || (boxMode === 'optional' && root.dataset.fpsBoxPrechecked === 'true'),
      boxQty: Math.min(boxMax, Math.max(1, Number(root.dataset.fpsBoxDefaultQty) || 1)),
      adding: false
    };

    // Opt-in for optional boxes only: selecting 2 jewelry items proposes 2 boxes.
    // Synchronize on INITIAL LOAD (if prechecked) and on the NEXT jewelry quantity
    // change. Never synchronize inside paint(): manual +/- edits must persist.
    function syncOptionalBoxQuantity() {
      if (syncBoxToMainEnabled && state.boxOn) state.boxQty = state.main;
    }
    syncOptionalBoxQuantity();

    const rewardDefinitions = rewardsEnabled ? $$('[data-fps-reward]').map((element) => ({
      group: element.dataset.rewardGroup === 'secondary' ? 'secondary' : 'main',
      threshold: Math.max(1, Number(element.dataset.rewardThreshold) || 1),
      id: Number(element.dataset.rewardId) || 0,
      quantity: Math.max(1, Number(element.dataset.rewardQuantity) || 1),
      price: Math.max(0, Number(element.dataset.rewardPrice) || 0),
      compare: Math.max(0, Number(element.dataset.rewardCompare) || 0),
      available: element.dataset.rewardAvailable === 'true',
      useMainVariant: element.dataset.rewardUseMainVariant === 'true',
      title: element.dataset.rewardTitle || word('fpsRewardsCardLabel', 'Cadeau offert'),
      onCard: element.dataset.rewardShowCard !== 'false',
      inSummary: element.dataset.rewardShowSummary !== 'false',
      showValue: element.dataset.rewardShowValue === 'true'
    })) : [];
    // Highest eligible threshold wins PER qualifying product.
    // Example: buy 2 -> box; buy 3 -> box + ring requires all gifts at tier 3.
    // Lower-tier rewards are not stacked automatically.
    function activeRewards(mainQty, secondaryQty) {
      return ['main', 'secondary'].flatMap((group) => {
        const q = group === 'main' ? mainQty : secondaryQty;
        const eligible = rewardDefinitions.filter((reward) => reward.group === group && reward.threshold <= q && ((reward.available && reward.id) || (reward.useMainVariant && getVariant(mainVariant, 'main').available)));
        if (!eligible.length) return [];
        const highest = Math.max(...eligible.map((reward) => reward.threshold));
        return eligible.filter((reward) => reward.threshold === highest).map((reward) => {
          if (!reward.useMainVariant) return reward;
          const selected = getVariant(mainVariant, 'main');
          return {...reward, id: selected.id, price: selected.price, compare: selected.compare, available: selected.available};
        });
      });
    }
    function rewardDescription(reward) {
      const label = reward.title || word('fpsRewardsCardLabel', 'Cadeau offert');
      const qty = reward.quantity > 1 ? `${reward.quantity} × ` : '';
      const value = reward.showValue ? ` (${money(reward.price * reward.quantity)} de valeur)` : '';
      return `${qty}${label}${value}`;
    }
    function updateGiftCards(group, count, q, button) {
      const cell = button.querySelector('[data-fps-tier-gifts]');
      if (!cell) return;
      const gifts = activeRewards(group === 'main' ? q : state.main, group === 'secondary' ? q : state.secondary)
        .filter((reward) => reward.group === group && reward.onCard);
      cell.textContent = gifts.map(rewardDescription).join(' · ');
      cell.hidden = gifts.length === 0;
    }
    function renderGiftSummary() {
      const panel = $('[data-fps-qty-reward-summary]');
      const list = $('[data-fps-qty-reward-list]');
      if (!panel || !list) return;
      const gifts = activeRewards(state.main, state.secondary).filter((r) => r.inSummary);
      list.replaceChildren();
      gifts.forEach((reward) => {
        const item = document.createElement('li');
        item.textContent = rewardDescription(reward);
        list.appendChild(item);
      });
      panel.hidden = gifts.length === 0;
    }
    function currentSecondary() {
      if (secondFixed) return Array.from({ length: state.secondary }, () => getVariant(secondFixed, 'secondary'));
      return $$('[data-fps-qty-secondary-variant]').map((node) => getVariant(node, 'secondary'));
    }
    function getBoxVariant() {
      const selected = boxChoices.find((button) => button.classList.contains('is-selected') && !button.disabled) || boxChoices.find((button) => !button.disabled);
      if (!selected) return {id: 0, price: 0, compare: 0, available: false};
      const price = Number(selected.dataset.price) || 0;
      return {
        id: Number(selected.dataset.variantId) || 0,
        price,
        compare: comparePrice('box', price, selected.dataset.compare),
        available: selected.dataset.available === 'true'
      };
    }
    function currentBox() {
      if (!boxOffer || !state.boxOn) return [];
      return Array.from({length: pairedBox ? state.main : state.boxQty}, getBoxVariant);
    }
    function boxCost(items) {
      return {
        items,
        selling: items.reduce((sum, item) => sum + item.price, 0),
        compare: items.reduce((sum, item) => sum + item.compare, 0),
        total: items.reduce((sum, item) => sum + item.price, 0)
      };
    }
    function fallbackSecondary() {
      if (secondFixed) return getVariant(secondFixed, 'secondary');
      const templateSelect = secondTemplate?.content?.querySelector('select');
      const defaultOption = templateSelect &&
        (Array.from(templateSelect.options).find((option) => option.selected && !option.disabled) ||
         Array.from(templateSelect.options).find((option) => !option.disabled));
      return getVariant(defaultOption, 'secondary');
    }
    function secondaryForCount(n) {
      const existing = currentSecondary();
      const fallback = fallbackSecondary();
      return Array.from({ length: n }, (_, i) => existing[i] || fallback);
    }
    function mainForCount(n) {
      if (!perItemMain) return Array.from({ length: n }, () => getVariant(mainVariant));
      return Array.from({ length: n }, (_, index) => getVariant(mainOptionById(state.mainItemIds[index]), 'main'));
    }
    function refreshMainItemFields() {
      if (!perItemMain || !mainItemTemplate) return;
      const activeTier = Math.min(state.main, 3);
      mainItemTierShells.forEach(shell => {
        const button = shell.querySelector('[data-fps-qty-tier="main"]');
        const panel = shell.querySelector('[data-fps-qty-main-item-fields]');
        const host = shell.querySelector('[data-fps-qty-main-item-selectors]');
        const active = Number(button?.dataset.tierQuantity) === activeTier;
        if (!panel || !host) return;
        shell.classList.toggle('is-selected', active);
        if (button) button.setAttribute('aria-expanded', String(active));
        panel.hidden = !active;
        host.replaceChildren();
        if (!active) return;
        for (let index = 0; index < state.main; index++) {
          const fragment = mainItemTemplate.content.cloneNode(true);
          const picker = fragment.querySelector('[data-fps-qty-main-item-select]');
          const label = fragment.querySelector('[data-fps-qty-main-item-label]');
          const values = {index: index + 1, item: config.main.singular, items: config.main.plural};
          if (label) label.textContent = renderText(word('fpsVariantRow', '{item} {index}'), values);
          if (picker) {
            const option = mainOptionById(state.mainItemIds[index]);
            if (option) picker.value = option.value;
            picker.dataset.fpsQtyItemIndex = String(index);
            picker.setAttribute('aria-label', renderText(word('fpsVariantAria', 'Variante de {item} {index}'), values));
          }
          host.appendChild(fragment);
        }
      });
    }
    function cost(group, items) {
      const rules = config[group];
      const compare = items.reduce((sum, item) => sum + item.compare, 0);
      const selling = items.reduce((sum, item) => sum + item.price, 0);
      let discount = 0;
      if (items.length > 1) discount += Math.round(items[1].price * rules.second / 100);
      if (items.length > 2) discount += Math.round(items[2].price * rules.third / 100);
      for (let i = 3; i < items.length; i++) discount += Math.round(items[i].price * rules.extra / 100);
      return { compare, selling, total: Math.max(0, selling - discount), items };
    }
    function tierValues(group, quantity, discount) {
      const rules = config[group];
      return { qty: quantity, item: rules.singular, items: rules.plural, discount, second: rules.second, third: rules.third, amount: '' };
    }
    function tierTitle(group, q) {
      const c = config[group];
      const template = q === 1 ? word('fpsTierLabelOne', '{qty} {item}') :
        q === 2 ? word('fpsTierLabelMany', '{qty} {items}') : word('fpsTierLabelMore', '{qty}+ {items}');
      const values = tierValues(group, q, 0);
      if (pairedBox && group === 'main') {
        values.item = bundleNoun.singular;
        values.items = bundleNoun.plural;
      }
      return renderText(template, values);
    }
    function tierDescription(group, q) {
      const rules = config[group];
      const value = q === 2 ? rules.second : q === 3 ? rules.third : 0;
      const template = (rules.tierText[q - 1] || '').trim();
      // A merchant's custom label must win, including when the tier has a
      // 0% quantity discount (for example, "Livraison gratuite"). Only the
      // original, unedited discount placeholder falls back to "Prix actuel".
      const defaultDiscountLabel = `${q}ᵉ à −{discount} %`;
      if (!value && q > 1 && (!template || template === defaultDiscountLabel)) {
        return word('fpsNoDiscount', 'Prix actuel');
      }
      return renderText(template, tierValues(group, q, value));
    }
    function savingText(saving, summary = false) {
      const template = summary ? word('fpsSummarySavingsTemplate', 'Vous économisez {amount}') : word('fpsBadgeTemplate', '−{amount}');
      return renderText(template, {amount: money(saving), savings: money(saving)});
    }
    function updateTierCards(group, count) {
      $$(`[data-fps-qty-tier="${group}"]`).forEach((button) => {
        const q = Number(button.dataset.tierQuantity);
        const shownQty = q === 3 && count > 3 ? count : q;
        const items = group === 'main' ? mainForCount(shownQty) : secondaryForCount(shownQty);
        const jewelry = cost(group, items);
        const giftBox = group === 'main' && pairedBox ? boxCost(Array.from({length: shownQty}, getBoxVariant)) : boxCost([]);
        const price = {
          ...jewelry,
          total: jewelry.total + giftBox.total,
          compare: jewelry.compare + giftBox.compare,
          selling: jewelry.selling + giftBox.selling
        };
        const basis = showCompare ? price.compare : price.selling;
        const saved = Math.max(0, basis - price.total);
        const selected = q === Math.min(3, count);
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', String(selected));
        if (group === 'main' && perItemMain) {
          button.closest('[data-fps-qty-main-tier-shell]')?.classList.toggle('is-selected', selected);
          button.setAttribute('aria-expanded', String(selected));
        }
        button.disabled = items.some((item) => !item.id || !item.available) ||
          (group === 'main' && pairedBox && (!getBoxVariant().id || !getBoxVariant().available));
        const title = button.querySelector('[data-fps-qty-tier-title]');
        const amount = button.querySelector('[data-fps-qty-tier-price]');
        const previous = button.querySelector('[data-fps-qty-tier-compare]');
        const badge = button.querySelector('[data-fps-qty-tier-badge]');
        const note = button.querySelector('[data-fps-qty-tier-note]');
        if (title) title.textContent = tierTitle(group, q);
        if (amount) amount.textContent = money(price.total);
        if (previous) {
          previous.hidden = !showCompare || saved <= 0;
          previous.textContent = money(price.compare);
        }
        if (badge) {
          badge.hidden = !showSavings || saved <= 0;
          badge.textContent = savingText(saved);
        }
        if (note) note.textContent = tierDescription(group, q);
        updateGiftCards(group, count, shownQty, button);
      });
      const stepper = $(`[data-fps-qty-stepper="${group}"]`);
      if (stepper) stepper.hidden = count < 3;
      const countLabel = $(`[data-fps-qty-stepper-count="${group}"]`);
      if (countLabel) countLabel.textContent = String(count);
      const minus = $(`[data-fps-qty-stepper-minus="${group}"]`);
      const plus = $(`[data-fps-qty-stepper-plus="${group}"]`);
      if (minus) minus.disabled = count <= 3;
      if (plus) plus.disabled = count >= max;
    }
    function refreshSecondaryFields() {
      if (!secondTemplate || !secondFields) return;
      const previousIds = currentSecondary().map((item) => item.id);
      secondFields.replaceChildren();
      for (let i = 0; i < state.secondary; i++) {
        const fragment = secondTemplate.content.cloneNode(true);
        const label = fragment.querySelector('[data-fps-qty-variant-label]');
        const picker = fragment.querySelector('[data-fps-qty-secondary-variant]');
        const values = {index: i + 1, item: config.secondary.singular, items: config.secondary.plural};
        if (label) label.textContent = renderText(word('fpsVariantRow', '{item} {index}'), values);
        if (picker) {
          const previous = previousIds[i];
          const choices = Array.from(picker.options);
          const selected = choices.find((item) => Number(item.value) === previous && !item.disabled) || choices.find((item) => item.selected && !item.disabled) || choices.find((item) => !item.disabled);
          picker.value = selected?.value || '';
          picker.setAttribute('aria-label', renderText(word('fpsVariantAria', 'Variante de {item} {index}'), values));
        }
        secondFields.appendChild(fragment);
      }
    }
    function say(message, kind = '') {
      if (!status) return;
      status.textContent = message;
      status.dataset.kind = kind;
    }
    function summaryLabel(group, qty) {
      const rules = config[group];
      return `${qty} ${qty === 1 ? rules.singular : rules.plural}${rules.summaryName ? ` ${rules.summaryName}` : ''}`;
    }
    // Render the same live quantities and savings outside the purchase module.
    // Do not borrow the fixed jewelry+box totals: those are different offers.
    function updatePageOfferMetrics({main, second, box, total, regular, saved}) {
      const page = root.closest('.fps');
      if (!page) return;
      const mainSaved = Math.max(0, main.compare - main.total);
      const boxSaved = Math.max(0, box.compare - box.total);
      const qtySaved = Math.max(0, main.selling + second.selling - main.total - second.total);
      const percent = (part, base) => base > 0 ? Math.round(part * 100 / base) : 0;
      const modeValue = (mode) => {
        switch (mode) {
          case 'selection_total': return {value: total, text: moneyPromo(total), allowZero: true};
          case 'quantity_savings': return {value: qtySaved, text: moneyPromo(qtySaved)};
          case 'main_savings': return {value: mainSaved, text: moneyPromo(mainSaved)};
          case 'third_item_percent': return {value: config.main.third, text: `−${config.main.third} %`};
          default: return {value: saved, text: moneyPromo(saved)};
        }
      };
      page.querySelectorAll('[data-fps-qty-live-amount], [data-fps-qty-hero-live]').forEach((element) => {
        const metric = modeValue(element.dataset.fpsQtyValueMode);
        const visible = Boolean(metric.allowZero || metric.value > 0);
        element.textContent = metric.text;
        element.hidden = !visible;
        const wrap = element.closest('[data-fps-qty-live-container]');
        if (wrap) wrap.hidden = !visible;
      });
      // Preserve the existing hero mode options, interpreting their amounts
      // against the selected quantity offer rather than a missing heart box.
      const heroMetrics = {
        bundle_amount: {value: saved, text: `−${moneyPromo(saved)}`},
        bundle_percent: {value: saved, text: `−${percent(saved, regular)} %`},
        main_amount: {value: mainSaved, text: `−${moneyPromo(mainSaved)}`},
        main_percent: {value: mainSaved, text: `−${percent(mainSaved, main.compare)} %`},
        box_amount: {value: boxSaved, text: `−${moneyPromo(boxSaved)}`},
        box_percent: {value: boxSaved, text: `−${percent(boxSaved, box.compare)} %`}
      };
      page.querySelectorAll('[data-fps-qty-original-percent]').forEach((element) => {
        const amount = percent(saved, regular);
        element.textContent = `−${amount} %`;
        const group = element.closest('[data-fps-qty-original-percent-group]');
        if (group) group.hidden = saved <= 0;
      });
      page.querySelectorAll('[data-fps-hero-metric]').forEach((element) => {
        const metric = heroMetrics[element.dataset.fpsHeroMetric];
        if (!metric) return;
        element.textContent = metric.text;
        const wrap = element.closest('[data-fps-hero-metric-group]');
        if (wrap) wrap.hidden = metric.value <= 0;
      });
      // Custom offer-strip cards: every tile can show any supported live metric.
      // These are display previews, not new checkout/Koala discount rules.
      const discountBasis = showCompare ? regular : main.selling + second.selling + box.selling;
      const metricData = (amount, available = true, pct = false) => ({text: pct ? `−${Math.round(amount)} %` : moneyPromo(amount), available});
      const two = cost('main', mainForCount(2));
      const three = cost('main', mainForCount(3));
      const twoBoxes = pairedBox ? boxCost(Array.from({length: 2}, getBoxVariant)) : boxCost([]);
      const threeBoxes = pairedBox ? boxCost(Array.from({length: 3}, getBoxVariant)) : boxCost([]);
      const tier2Total = two.total + twoBoxes.total;
      const tier3Total = three.total + threeBoxes.total;
      const tier2Compare = two.compare + twoBoxes.compare;
      const tier3Compare = three.compare + threeBoxes.compare;
      const mainVariantData = getVariant(mainVariant, 'main');
      const mainReady = Boolean(mainVariantData.id && mainVariantData.available);
      const boxSelected = box.items.length > 0;
      const unlockedGifts = activeRewards(state.main, state.secondary);
      const giftValue = unlockedGifts.reduce((sum, gift) => sum + gift.price * gift.quantity, 0);
      // Build complete offer-card lines from the SAME variant/quantity math as
      // the purchase module. These are never separate discount rules.
      const boxVariant = getBoxVariant();
      const boxAvailable = boxMode !== 'off' && Boolean(boxVariant.id && boxVariant.available);
      const boxUnitSaving = Math.max(0, boxVariant.compare - boxVariant.price);
      const secondSaving = Math.max(0, second.compare - second.total);
      const dealData = (price, compare, available, style = 'savings') => ({
        kind: 'deal', price, compare, available, style
      });
      const dynamicValues = {
        selection_total: metricData(total, mainReady),
        selection_compare: metricData(regular, mainReady),
        selection_savings: metricData(saved, saved > 0),
        selection_percent: metricData(discountBasis > 0 ? saved * 100 / discountBasis : 0, saved > 0, true),
        quantity_savings: metricData(qtySaved, qtySaved > 0),
        main_total: metricData(main.total, mainReady),
        main_compare: metricData(main.compare, mainReady),
        main_savings: metricData(mainSaved, mainSaved > 0),
        main_unit_price: metricData(mainVariantData.price, mainReady),
        main_unit_compare: metricData(mainVariantData.compare, mainReady),
        main_tier2_price: metricData(tier2Total, mainReady),
        main_tier2_savings: metricData(Math.max(0, tier2Compare - tier2Total), mainReady && tier2Compare > tier2Total),
        main_tier3_price: metricData(tier3Total, mainReady),
        main_tier3_savings: metricData(Math.max(0, tier3Compare - tier3Total), mainReady && tier3Compare > tier3Total),
        main_second_percent: metricData(config.main.second, config.main.second > 0, true),
        main_third_percent: metricData(config.main.third, config.main.third > 0, true),
        secondary_total: metricData(second.total, second.items.length > 0),
        secondary_savings: metricData(secondSaving, second.items.length > 0 && secondSaving > 0),
        box_total: metricData(box.total, boxSelected),
        // The standalone box DEAL remains visible even before an optional box
        // is checked; its price is a real selected Shopify variant price.
        box_unit_price: metricData(boxVariant.price, boxAvailable),
        box_savings: metricData(boxSaved, boxSelected && boxSaved > 0),
        box_percent: metricData(boxVariant.compare > 0 ? 100 * boxUnitSaving / boxVariant.compare : 0, boxAvailable && boxUnitSaving > 0, true),
        active_rewards: {text: unlockedGifts.map(rewardDescription).join(' · '), available: unlockedGifts.length > 0},
        reward_value: metricData(giftValue, unlockedGifts.length > 0 && giftValue > 0),
        main_unit_deal: dealData(mainVariantData.price, mainVariantData.compare, mainReady),
        main_selected_deal: dealData(main.total, main.compare, mainReady),
        main_tier2_deal: dealData(tier2Total, tier2Compare, mainReady),
        main_tier3_deal: dealData(tier3Total, tier3Compare, mainReady),
        selection_deal: dealData(total, regular, mainReady),
        box_unit_deal: dealData(boxVariant.price, boxVariant.compare, boxAvailable, 'percent'),
        box_selected_deal: dealData(box.total, box.compare, boxSelected, 'percent'),
        secondary_selected_deal: dealData(second.total, second.compare, second.items.length > 0)
      };
      page.querySelectorAll('[data-fps-offer-dynamic-metric]').forEach((element) => {
        const metric = dynamicValues[element.dataset.fpsOfferDynamicMetric];
        element.hidden = !metric?.available;
        element.classList.toggle('fps-offer-custom-value--deal', Boolean(metric?.available && metric.kind === 'deal'));
        if (element.hidden) { element.replaceChildren(); return; }
        const prefix = element.dataset.fpsOfferDynamicPrefix?.trim();
        const suffix = element.dataset.fpsOfferDynamicSuffix?.trim();
        if (metric.kind !== 'deal') {
          element.textContent = [prefix, metric.text, suffix].filter(Boolean).join(' ');
          return;
        }
        const parts = document.createDocumentFragment();
        if (prefix) parts.append(document.createTextNode(`${prefix} `));
        const price = document.createElement('span');
        price.className = 'fps-offer-custom-value__price';
        price.textContent = moneyPromo(metric.price);
        parts.append(price);
        if (metric.compare > metric.price) {
          const was = element.dataset.fpsOfferDynamicWas?.trim() || 'au lieu de';
          parts.append(document.createTextNode(` ${was} `));
          const compare = document.createElement('s');
          compare.textContent = moneyPromo(metric.compare);
          parts.append(compare);
          const saving = Math.max(0, metric.compare - metric.price);
          const last = metric.style === 'percent'
            ? `−${Math.round(100 * saving / metric.compare)} %`
            : `${element.dataset.fpsOfferDynamicSave?.trim() || 'Économisez'} ${moneyPromo(saving)}`;
          parts.append(document.createTextNode(` · ${last}`));
        }
        if (suffix) parts.append(document.createTextNode(` ${suffix}`));
        element.replaceChildren(parts);
      });
      page.querySelectorAll('[data-fps-qty-strip-main-price]').forEach((element) => {
        element.textContent = money(main.total);
        element.hidden = !main.items.length;
      });
      page.querySelectorAll('[data-fps-qty-strip-selected-savings]').forEach((element) => {
        element.textContent = savingText(saved, true);
        element.hidden = saved <= 0;
      });
    }

    // Mode 3 uses the SAME fixed-bundle price presentation as Mode 1.
    // Only the quantities and values change. Mode 2 is not affected.
    function updateBundleBreakdown(main, second, box, total, regular, saved) {
      if (!pairedBox) return;
      const put = (selector, text, hide = false) => {
        const element = $(selector);
        if (!element) return;
        element.textContent = text;
        element.hidden = hide;
      };
      // With multiple gifts, make the number of jewelry items AND boxes
      // explicit, while reusing the merchant's original fixed-bundle labels.
      ['main', 'box'].forEach((part) => {
        const label = $(`[data-fps-qty-bundle-${part}-label]`);
        if (!label) return;
        const source = label.dataset[`fpsQtyBundle${part.charAt(0).toUpperCase()}${part.slice(1)}Label`] || label.textContent;
        label.textContent = state.main > 1 ? `${state.main} × ${source}` : source;
      });
      put('[data-fps-qty-bundle-main-price]', money(main.total));
      put('[data-fps-qty-bundle-main-compare]', money(main.compare), !showCompare || main.compare <= main.total);
      put('[data-fps-qty-bundle-box-price]', money(box.total));
      put('[data-fps-qty-bundle-box-compare]', money(box.compare), !showCompare || !boxShowCompare || box.compare <= box.total);
      const boxPct = box.compare > 0 ? Math.round(100 * Math.max(0, box.compare - box.total) / box.compare) : 0;
      put('[data-fps-qty-bundle-box-percent]', `−${boxPct} %`, !boxShowBadge || boxPct <= 0);
      const secRow = $('[data-fps-qty-bundle-secondary-line]');
      if (secRow) secRow.hidden = state.secondary === 0;
      if (state.secondary) {
        put('[data-fps-qty-bundle-secondary-label]', summaryLabel('secondary', state.secondary));
        put('[data-fps-qty-bundle-secondary-price]', money(second.total));
        put('[data-fps-qty-bundle-secondary-compare]', money(second.compare), !showCompare || second.compare <= second.total);
      }
      put('[data-fps-qty-bundle-total]', money(total));
      put('[data-fps-qty-bundle-regular]', money(regular), !showCompare || regular <= total);
      const badge = $('[data-fps-qty-bundle-savings]');
      if (badge) {
        const label = badge.dataset.fpsBundleSaveLabel || '';
        badge.textContent = label ? `${label} ${money(saved)}` : money(saved);
        badge.hidden = !showSavings || saved <= 0;
      }
    }

    function paint() {
      const main = cost('main', mainForCount(state.main));
      const second = cost('secondary', state.secondary ? currentSecondary() : []);
      const box = boxCost(currentBox());
      if (pairedBox) { state.boxOn = true; state.boxQty = state.main; }
      if (boxDetails) boxDetails.hidden = !state.boxOn;
      if (boxSummary) boxSummary.hidden = !state.boxOn;
      if (boxToggle) boxToggle.checked = state.boxOn;
      const chosenBox = boxChoices.find((button) => button.classList.contains('is-selected'));
      const boxVariant = getBoxVariant();
      const boxHeadlinePrice = $('[data-fps-qty-box-offer-price]');
      if (boxHeadlinePrice) boxHeadlinePrice.textContent = money(boxVariant.price);
      const boxCompare = $('[data-fps-qty-box-offer-compare]');
      const boxBadge = $('[data-fps-qty-box-offer-badge]');
      const difference = Math.max(0, boxVariant.compare - boxVariant.price);
      if (boxCompare) {
        boxCompare.textContent = money(boxVariant.compare);
        boxCompare.hidden = !boxShowCompare || difference <= 0;
      }
      if (boxBadge) {
        const percent = boxVariant.compare > 0 ? Math.round(100 * difference / boxVariant.compare) : 0;
        const template = root.dataset.fpsBoxBadgeTemplate || '';
        const rendered = template ? renderText(template, {percent, amount: money(difference)}) : '';
        boxBadge.textContent = rendered;
        boxBadge.hidden = !boxShowBadge || difference <= 0 || !rendered;
      }
      const boxPickedName = $('[data-fps-qty-box-variant-label]');
      if (boxPickedName && chosenBox) boxPickedName.textContent = chosenBox.getAttribute('aria-label') || '';
      const boxCountLabel = $('[data-fps-qty-box-count]');
      if (boxCountLabel) boxCountLabel.textContent = String(pairedBox ? state.main : state.boxQty);
      const boxMinus = $('[data-fps-qty-box-minus]');
      const boxPlus = $('[data-fps-qty-box-plus]');
      if (boxMinus) boxMinus.disabled = state.boxQty <= 1;
      if (boxPlus) boxPlus.disabled = state.boxQty >= Math.max(boxMax, syncBoxToMainEnabled ? state.main : 1);
      updateTierCards('main', state.main);
      if (secondArea) secondArea.hidden = state.secondary === 0;
      if (secondToggle) {
        secondToggle.textContent = state.secondary ? word('fpsSecondRemove', 'Retirer') : word('fpsSecondAdd', '+ Ajouter');
        secondToggle.setAttribute('aria-expanded', String(state.secondary > 0));
      }
      const promo = $('[data-fps-qty-second-promo]');
      if (promo) {
        const template = word('fpsSecondPromo', '');
        const result = renderText(template, {second: config.secondary.second, third: config.secondary.third});
        promo.textContent = result;
        promo.hidden = !result;
      }
      if (state.secondary) updateTierCards('secondary', state.secondary);
      if (summarySecond) summarySecond.hidden = state.secondary === 0;
      const mainText = $('[data-fps-qty-summary-label="main"]');
      const mainValue = $('[data-fps-qty-summary-value="main"]');
      if (mainText) mainText.textContent = summaryLabel('main', state.main);
      if (mainValue) mainValue.textContent = money(main.total);
      const secondText = $('[data-fps-qty-summary-label="secondary"]');
      const secondValue = $('[data-fps-qty-summary-value="secondary"]');
      if (secondText) secondText.textContent = summaryLabel('secondary', state.secondary);
      if (secondValue) secondValue.textContent = money(second.total);
      const boxText = $('[data-fps-qty-summary-label="box"]');
      const boxValue = $('[data-fps-qty-summary-value="box"]');
      const activeBoxQty = pairedBox ? state.main : state.boxQty;
      if (boxText) boxText.textContent = `${activeBoxQty} ${activeBoxQty === 1 ? word('fpsBoxSingular', 'boîte cadeau') : word('fpsBoxPlural', 'boîtes cadeau')}`;
      if (boxValue) boxValue.textContent = money(box.total);
      const total = main.total + second.total + box.total;
      const regular = main.compare + second.compare + box.compare;
      const selling = main.selling + second.selling + box.selling;
      const saved = Math.max(0, (showCompare ? regular : selling) - total);
      const totalElement = $('[data-fps-qty-total]');
      if (totalElement) totalElement.textContent = money(total);
      const regularRow = $('[data-fps-qty-summary-regular-row]');
      const regularValue = $('[data-fps-qty-summary-regular]');
      if (regularRow) regularRow.hidden = !showCompare || regular <= total;
      if (regularValue) regularValue.textContent = money(regular);
      const savingsRow = $('[data-fps-qty-summary-savings-row]');
      const savingsValue = $('[data-fps-qty-summary-saving]');
      if (savingsRow) savingsRow.hidden = !showSavings || saved <= 0;
      if (savingsValue) savingsValue.textContent = savingText(saved, true);
      renderGiftSummary();
      updateQuickViewPrice();
      updateBundleBreakdown(main, second, box, total, regular, saved);
      updatePageOfferMetrics({main, second, box, total, regular, saved});
      if (submit) {
        const all = [...main.items, ...second.items, ...box.items];
        submit.disabled = state.adding || all.some((item) => !item.id || !item.available) || second.items.length !== state.secondary || (pairedBox && (!boxOffer || !getBoxVariant().id || !getBoxVariant().available));
        submit.setAttribute('aria-disabled', String(submit.disabled));
      }
    }

    $$('[data-fps-qty-tier="main"]').forEach((button) => button.addEventListener('click', () => {
      const nextQuantity = Math.max(1, Number(button.dataset.tierQuantity) || 1);
      if (nextQuantity !== state.main) {
        state.main = nextQuantity;
        syncOptionalBoxQuantity();
        refreshMainItemFields();
      }
      say(''); paint();
    }));
    $$('[data-fps-qty-tier="secondary"]').forEach((button) => button.addEventListener('click', () => {
      state.secondary = Math.max(1, Number(button.dataset.tierQuantity) || 1);
      refreshSecondaryFields(); say(''); paint();
    }));
    ['main', 'secondary'].forEach((group) => {
      $(`[data-fps-qty-stepper-plus="${group}"]`)?.addEventListener('click', () => {
        state[group] = Math.min(max, state[group] + 1);
        if (group === 'main') { syncOptionalBoxQuantity(); refreshMainItemFields(); }
        if (group === 'secondary') refreshSecondaryFields();
        say(''); paint();
      });
      $(`[data-fps-qty-stepper-minus="${group}"]`)?.addEventListener('click', () => {
        state[group] = Math.max(3, state[group] - 1);
        if (group === 'main') { syncOptionalBoxQuantity(); refreshMainItemFields(); }
        if (group === 'secondary') refreshSecondaryFields();
        say(''); paint();
      });
    });
    // Per-item mode: a single actual Shopify variant ID per purchased unit.
    // Never nest a select inside the tier's button: the selected card has a
    // separate panel to preserve valid HTML, keyboard behavior and focus.
    root.querySelector('[data-fps-qty-main-tiers]')?.addEventListener('change', event => {
      const picker = event.target.closest('[data-fps-qty-main-item-select]');
      if (!perItemMain || !picker) return;
      const index = Number(picker.dataset.fpsQtyItemIndex);
      const chosen = selectedOption(picker);
      if (!Number.isInteger(index) || index < 0 || index >= state.main || !chosen || chosen.disabled) return;
      state.mainItemIds[index] = Number(chosen.value);
      if (index === 0 && mainVariant?.tagName === 'SELECT') {
        mainVariant.value = chosen.value;
        showSelectedMainVariantMedia();
      }
      say(''); paint();
    });
    mainOptionButtons.forEach((button) => button.addEventListener('click', () => selectMainVisualOption(button)));
    mainVariant?.addEventListener('change', () => {
      syncMainVisualOptions();
      showSelectedMainVariantMedia();
      say(''); paint();
    });
    secondToggle?.addEventListener('click', () => {
      state.secondary = state.secondary ? 0 : state.initialSecondary;
      refreshSecondaryFields(); say(''); paint();
    });
    secondFields?.addEventListener('change', (event) => {
      if (event.target.matches('[data-fps-qty-secondary-variant]')) { say(''); paint(); }
    });

    boxToggle?.addEventListener('change', () => {
      state.boxOn = boxToggle.checked;
      if (state.boxOn) syncOptionalBoxQuantity();
      say(''); paint();
    });
    boxChoices.forEach((button) => button.addEventListener('click', () => {
      boxChoices.forEach((item) => {
        const active = item === button;
        item.classList.toggle('is-selected', active);
        item.setAttribute('aria-pressed', String(active));
      });
      say(''); paint();
    }));
    $('[data-fps-qty-box-minus]')?.addEventListener('click', () => { state.boxQty = Math.max(1, state.boxQty - 1); paint(); });
    $('[data-fps-qty-box-plus]')?.addEventListener('click', () => {
      // The jewelry count may exceed the configured manual box limit;
      // never clamp an auto-matched quantity below the jewelry count.
      const effectiveLimit = Math.max(boxMax, syncBoxToMainEnabled ? state.main : 1);
      state.boxQty = Math.min(effectiveLimit, state.boxQty + 1);
      paint();
    });

    // Match the quick view to the chosen SECONDARY variant and the same
    // effective compare-at reference used by the offer tiers (including the
    // optional manual EUR reference). Never alter actual checkout prices.
    function updateQuickViewPrice() {
      if (!quickview) return;
      const variant = (state.secondary ? currentSecondary()[0] : null) || fallbackSecondary();
      const amount = quickview.querySelector('[data-fps-qty-quickview-price]');
      const compare = quickview.querySelector('[data-fps-qty-quickview-compare]');
      if (!variant || !variant.id) return;
      if (amount) amount.textContent = money(variant.price);
      if (compare) {
        const hasRealSaving = variant.compare > variant.price;
        compare.hidden = !hasRealSaving;
        compare.textContent = hasRealSaving ? money(variant.compare) : '';
      }
    }
    if (quickview) {
      const close = () => typeof quickview.close === 'function' ? quickview.close() : quickview.removeAttribute('open');
      $$('[data-fps-qty-open-quickview]').forEach((button) => button.addEventListener('click', () => {
        updateQuickViewPrice();
        if (typeof quickview.showModal === 'function') quickview.showModal();
        else quickview.setAttribute('open', '');
      }));
      // The review badge is informational inside this popup. Prevent Judge.me's
      // normal product-page anchor from jumping to a nonexistent section.
      quickview.querySelector('[data-fps-qty-quickview-rating]')?.addEventListener('click', (event) => {
        if (event.target.closest('a, .jdgm-prev-badge')) event.preventDefault();
      }, true);
      $('[data-fps-qty-close-quickview]')?.addEventListener('click', close);
      quickview.querySelector('[data-fps-qty-quickview-select]')?.addEventListener('click', () => {
        // Add the secondary product to this offer; never navigate away or reset an existing choice.
        if (!state.secondary) {
          state.secondary = state.initialSecondary;
          refreshSecondaryFields();
        }
        say('');
        paint();
        close();
        // Bring the quantity / variant selectors into view for the next choice.
        const target = secondArea || secondToggle;
        target?.scrollIntoView({behavior: 'smooth', block: 'center'});
        (secondArea?.querySelector('[data-fps-qty-tier="secondary"]') || secondToggle)?.focus({preventScroll: true});
      });
      quickview.addEventListener('click', (event) => { if (event.target === quickview) close(); });
      quickview.querySelectorAll('[data-fps-qty-quickview-thumb]').forEach((button) => button.addEventListener('click', () => {
        const mainPhoto = quickview.querySelector('[data-fps-qty-quickview-main]');
        if (!mainPhoto) return;
        mainPhoto.src = button.dataset.photoUrl || mainPhoto.src;
        mainPhoto.alt = button.dataset.photoAlt || '';
        quickview.querySelectorAll('[data-fps-qty-quickview-thumb]').forEach((thumb) => {
          const chosen = thumb === button;
          thumb.classList.toggle('is-active', chosen);
          thumb.setAttribute('aria-pressed', String(chosen));
        });
      }));
    }

    submit?.addEventListener('click', async () => {
      if (state.adding) return;
      const main = mainForCount(state.main);
      const secondary = state.secondary ? currentSecondary() : [];
      const boxes = currentBox();
      const gifts = rewardsEnabled && rewardCartMode === 'page'
        ? activeRewards(state.main, state.secondary).flatMap((reward) =>
            Array.from({length: reward.quantity}, () => ({id: reward.id, available: reward.available})))
        : [];
      const items = [...main, ...secondary, ...boxes, ...gifts];
      if (items.some((item) => !item.id || !item.available) || secondary.length !== state.secondary || (pairedBox && (!boxOffer || !getBoxVariant().id || !getBoxVariant().available))) {
        say(word('fpsStatusUnavailable', 'Veuillez choisir des variantes disponibles.'), 'error'); return;
      }
      const basket = new Map();
      for (const item of items) basket.set(item.id, (basket.get(item.id) || 0) + 1);
      state.adding = true; paint(); say(word('fpsStatusAdding', 'Ajout au panier…'));
      try {
        const response = await fetch(`${route}cart/add.js`, {
          method: 'POST',
          headers: {'Content-Type': 'application/json', Accept: 'application/json'},
          credentials: 'same-origin',
          body: JSON.stringify({items: [...basket].map(([id, quantity]) => ({id, quantity}))})
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(result.description || result.message || word('fpsStatusError', 'Impossible de mettre le panier à jour.'));
        say(word('fpsStatusSuccess', 'Articles ajoutés au panier.'), 'success');
        if (root.dataset.fpsAfterAdd === 'upcart' && typeof window.upcartOpenCart === 'function') {
          try { window.upcartOpenCart(); state.adding = false; paint(); return; }
          catch (_) { /* Cart redirect fallback. */ }
        }
        window.location.assign(route + (root.dataset.fpsAfterAdd === 'checkout' ? 'checkout' : 'cart'));
      } catch (error) {
        state.adding = false; paint();
        say(error instanceof Error ? error.message : word('fpsStatusError', 'Impossible de mettre le panier à jour.'), 'error');
      }
    });
    syncMainVisualOptions();
    refreshMainItemFields();
    paint();
  }
  function boot(container = document) {
    container.querySelectorAll('[data-fps-quantity-offer]').forEach(init);
    if (container.matches?.('[data-fps-quantity-offer]')) init(container);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(), {once:true});
  else boot();
  document.addEventListener('shopify:section:load', (event) => boot(event.target));
})();
